import { PaymentProviderType } from "shared";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import { When } from "react-if";

import { Toggle } from "../../../../../common/Atoms/Toggle";
import { InputTextAddon } from "../../../../../common/Atoms/InputTextAddon";
import { Button } from "../../../../../common/Atoms/Button";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { useEnableRPSPaymentsMutation, useDisableRPSPaymentsMutation } from "../../../../services/api/organisationApi/organisation";
import { TwoColFormRow } from "../../../Molecules/Form/TwoColFormRow";
import rpsLogo from "../../../../assets/cloud-depot-logo.png";

export function RPSPayments() {
  const [enableRPSPayments, { isLoading: isEnableLoading }] = useEnableRPSPaymentsMutation();
  const [disableRPSPayments, { isLoading: isDisableLoading }] = useDisableRPSPaymentsMutation();
  const currentOrg = useGetSelectedOrganisation();

  const [paymentUrl, setPaymentUrl] = useState(``);
  const [token, setToken] = useState(``);
  const [showRPSSettings, setShowRPSSettings] = useState(false);

  const { currentProvider, enabled } = useMemo(() => {
    // Look for a RPS payment provider to identify it as RPS
    const currentProvider = currentOrg?.paymentProviders.find(p =>
      p.type === PaymentProviderType.RPS,
    );

    return {
      enabled: !!currentProvider,
      currentProvider,
    };
  }, [currentOrg?.paymentProviders]);

  // Update the form state when provider changes
  useEffect(() => {
    if (currentProvider?.payment_link_template) {
      // Parse the existing payment template to extract URL and token
      const template = currentProvider.payment_link_template;

      // Extract payment URL (everything before the query string)
      const urlMatch = template.match(/^([^?]+)/);
      const baseUrl = urlMatch ? urlMatch[1] : ``;

      // Extract token from query parameters
      const tokenMatch = template.match(/[?&]token=([^&]+)/);
      const extractedToken = tokenMatch ? tokenMatch[1] : ``;

      setPaymentUrl(baseUrl);
      setToken(extractedToken);
      setShowRPSSettings(true); // Show settings if there's existing config
    }
    else if (!currentProvider) {
      setPaymentUrl(``);
      setToken(``);
      setShowRPSSettings(false);
    }
  }, [currentProvider]);

  function generateCustomUrl() {
    if (!paymentUrl) {
      return ``;
    }

    // Construct the custom payment link template following the pattern
    const baseUrl = paymentUrl.endsWith(`/`) ? paymentUrl.slice(0, -1) : paymentUrl;
    let url = `${baseUrl}/?`;

    if (token) {
      url += `token=${token.trim()}&`;
    }

    url += `invoiceNo={{{InvoiceNumber}}}&currency={{{InvoiceCurrency}}}&amount={{{DueAmount}}}&shortCode={{{ShortCode}}}`;

    return url;
  }

  const formDirty = useMemo(() => {
    if (!currentProvider?.payment_link_template) {
      return showRPSSettings && (paymentUrl || token);
    }

    // Compare current form values with what would be generated
    const currentGeneratedUrl = generateCustomUrl();

    return currentGeneratedUrl !== currentProvider.payment_link_template;
  }, [paymentUrl, token, currentProvider, showRPSSettings]);

  function handleToggleChange() {
    if (!enabled) {
      handleEnable();
    }
    else {
      disableRPSPayments(currentOrg.id);
    }
  }

  function handleEnable() {
    if (!paymentUrl || !token) {
      toast.error(`Please enter a payment URL and token before enabling RPS.`);

      return;
    }

    const customUrl = generateCustomUrl();

    enableRPSPayments({
      organisationId: currentOrg.id,
      paymentLinkTemplate: customUrl,
    });
  }

  function handleSaveChanges() {
    handleEnable();
  }

  return (
    <TwoColFormRow
      heading={ `` }
      description={
        <img src={ rpsLogo }
          className={ `h-10` }
          alt={ `RPS` } />
      }
    >
      <Toggle
        label={ `Enable RPS Payments` }
        description={ `Adds ability to add RPS payment links to emails. Adds pay now buttons to statement PDFs & customer portal pages.` }
        checked={ enabled }
        loading={ isEnableLoading || isDisableLoading }
        onChange={ handleToggleChange }
        className={ `mb-4` }
      />

      <When condition={ !showRPSSettings }>
        <button
          onClick={ () => setShowRPSSettings(true) }
          className={ `text-sm text-blue-600 hover:text-blue-800 underline bg-transparent border-none cursor-pointer` }
        >
          { `+ Add RPS settings` }
        </button>
      </When>

      <When condition={ showRPSSettings }>
        <div className={ `space-y-4` }>
          <div>
            <InputTextAddon
              label={ `RPS Payment URL` }
              placeholder={ `https://your-rps-payment-url.com` }
              value={ paymentUrl }
              onChange={ e => setPaymentUrl(e.value as string) }
              disabled={ isEnableLoading || isDisableLoading }
            />
          </div>

          <div>
            <InputTextAddon
              label={ `RPS Token` }
              placeholder={ `AzP2Pkpmww337UUnUetpC5jiY` }
              value={ token }
              onChange={ e => setToken((e.value as string)?.trim()) }
              disabled={ isEnableLoading || isDisableLoading }
            />
          </div>

          <When condition={ paymentUrl }>
            <div className={ `p-3 bg-gray-50 rounded-sm border` }>
              <Paragraph variant={ `help` }
                className={ `mb-2` }>
                { `Generated RPS link:` }
              </Paragraph>
              <code className={ `text-xs break-all` }>
                { generateCustomUrl() }
              </code>
            </div>
          </When>

          <When condition={ formDirty }>
            <div className={ `flex justify-end` }>
              <Button
                color={ `orange` }
                onClick={ handleSaveChanges }
                disabled={ isEnableLoading || isDisableLoading }
              >
                { `Save Changes` }
              </Button>
            </div>
          </When>
        </div>
      </When>
    </TwoColFormRow>
  );
}
